import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { invalidateMinicart } from "~/app/store/bffApi";
import { useSession } from "~/app/providers/SessionProvider";
import { useCountryOptions } from "~/domains/referenceData/hooks/useFormOptions";
import {
  ConfigurationNameDialog,
  ConfiguredProductAddToCartDialog,
  RequestAQuoteDialog,
  RequestConfigurationQuoteDialog,
  type RequestAQuoteFormData,
} from "~/domains/configurator/components/dialogs";
import {
  CART_PAGE,
  LINEAR_CONFIGURATION_REQUEST_QUOTE_CONFIRMATION_PAGE_URL,
  SAVED_CONFIGS_PAGE,
  URL_CONTACT_US,
} from "~/utils/url/staticUrls";
import { redirectToUrl } from "~/utils/url/urlUtils";
import type { ConfiguratorCommerceRuntime } from "../../application/useConfiguratorCommerceRuntime";
import type { ConfiguratorConfiguration } from "../../contract/configuration";
import type { ConfiguratorRuntimeContext } from "../../contract/context";
import {
  addConfiguratorV2ToCart,
  downloadConfiguratorV2Datasheet,
  requestConfiguratorV2Quotation,
  requestConfiguratorV2Quote,
  saveConfiguratorV2,
  type ConfiguratorV2AddToCartResponseItem,
  type ConfiguratorV2PdfData,
} from "../../data/configuratorV2CommerceApi";
import {
  configuratorConfigurationKey,
  type ConfiguratorRuntimeQueryData,
} from "../../data/configuratorConfigurationQuery";
import type { ConfiguratorSummaryViewModel } from "../preview/buildConfiguratorPreviewViewModel";
import type { ConfiguratorSummaryActionId, SummaryAudienceCapabilities } from "./summaryCapabilities";
import { configuratorV2SummaryAnalytics } from "./configuratorV2SummaryAnalytics";

type ActiveDialog = "requestQuotation" | "requestOffer" | "saveConfiguration" | "addToCart" | "addToQuote" | "success";

export type ConfiguratorCartSuccessRenderer = (props: {
  onClose: () => void;
  responseItems: ConfiguratorV2AddToCartResponseItem[];
}) => ReactNode;

interface SummaryInteractionsInput {
  configuration: ConfiguratorConfiguration;
  context: ConfiguratorRuntimeContext;
  audience: SummaryAudienceCapabilities;
  commerce: ConfiguratorCommerceRuntime;
  preview: ConfiguratorSummaryViewModel;
  priceText: string;
  actionEnabled: (action: ConfiguratorSummaryActionId) => boolean;
  renderCartSuccess: ConfiguratorCartSuccessRenderer;
  onRegistrationRequested: () => void;
}

const buildPdfData = (
  configuration: ConfiguratorConfiguration,
  preview: ConfiguratorSummaryViewModel,
): ConfiguratorV2PdfData => ({
  designation: preview.designation ?? configuration.productShortDesignation,
  description: preview.description ?? "",
  manufacturerLogoUrl: "",
  brandLogoUrl: "",
  imageUrl: preview.imageUrl ?? "",
  steps: preview.sections.map((section) => ({
    title: section.title,
    substepData: [
      {
        name: section.title,
        headerRow: [],
        dataRows: section.rows.map((row) => ({
          items: [
            { value: row.label, standard: false },
            { value: row.value, standard: false },
          ],
        })),
      },
    ],
  })),
});

export const useConfiguratorSummaryInteractions = ({
  configuration,
  context,
  audience,
  commerce,
  preview,
  priceText,
  actionEnabled,
  renderCartSuccess,
  onRegistrationRequested,
}: SummaryInteractionsInput) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const countryOptions = useCountryOptions();
  const [activeDialog, setActiveDialog] = useState<ActiveDialog | null>(null);
  const [directPending, setDirectPending] = useState<ConfiguratorSummaryActionId | null>(null);
  const [directError, setDirectError] = useState<string | null>(null);
  const [addToCartError, setAddToCartError] = useState<string>();
  const [addToCartPending, setAddToCartPending] = useState(false);
  const [successItems, setSuccessItems] = useState<ConfiguratorV2AddToCartResponseItem[]>([]);
  const analyticsInput = useMemo(
    () => ({
      productCode: configuration.productCode,
      currentStepNumber: configuration.currentStepNumber,
      designation: configuration.productShortDesignation,
    }),
    [configuration.currentStepNumber, configuration.productCode, configuration.productShortDesignation],
  );
  const pdfData = useMemo(() => buildPdfData(configuration, preview), [configuration, preview]);

  const closeDialog = useCallback(() => {
    setActiveDialog(null);
    setAddToCartError(undefined);
  }, []);

  const triggerAction = useCallback(
    (action: ConfiguratorSummaryActionId) => {
      if (!actionEnabled(action) || activeDialog || directPending) {
        return;
      }
      setDirectError(null);
      if (audience.audience === "public" && (action === "requestOffer" || action === "saveConfiguration")) {
        if (action === "requestOffer") {
          configuratorV2SummaryAnalytics.requestOffer(analyticsInput);
        }
        onRegistrationRequested();
        return;
      }
      if (
        action === "requestQuotation" ||
        action === "requestOffer" ||
        action === "saveConfiguration" ||
        action === "addToCart" ||
        action === "addToQuote"
      ) {
        if (action === "requestOffer") {
          configuratorV2SummaryAnalytics.requestOffer(analyticsInput);
        }
        setActiveDialog(action);
        return;
      }
      if (action === "goToCart") {
        redirectToUrl(CART_PAGE);
        return;
      }
      if (action === "contactSpecialist") {
        window.open(context.customization.specialistLinkURL || URL_CONTACT_US, "_blank", "noopener,noreferrer");
        return;
      }

      setDirectPending(action);
      void downloadConfiguratorV2Datasheet(configuration.productCode, pdfData)
        .catch((error) => {
          console.error(error);
          setDirectError(t("hybrisAjaxError"));
        })
        .finally(() => setDirectPending(null));
    },
    [
      actionEnabled,
      activeDialog,
      analyticsInput,
      audience.audience,
      configuration.productCode,
      context.customization.specialistLinkURL,
      directPending,
      onRegistrationRequested,
      pdfData,
      t,
    ],
  );

  const submitName = async (name: string) => {
    const saved = await saveConfiguratorV2(
      configuration.productCode,
      configuration.configId,
      name,
      configuration.currentStepNumber,
    );
    configuratorV2SummaryAnalytics.saved(analyticsInput);
    if (activeDialog === "requestOffer") {
      await navigate({
        to: "/store-finder",
        state: { shareLinearConfigurationData: [saved.code] },
      });
    } else {
      redirectToUrl(SAVED_CONFIGS_PAGE);
    }
  };

  const submitAddToCart = async () => {
    if (commerce.isSettling || addToCartPending) {
      return;
    }
    setAddToCartError(undefined);
    setAddToCartPending(true);
    try {
      const response = await addConfiguratorV2ToCart({
        configId: configuration.configId,
        currentStepNumber: configuration.currentStepNumber,
        quantity: commerce.quantity,
        priceReference: commerce.priceReference,
      });
      const current = queryClient.getQueryData<ConfiguratorRuntimeQueryData>(
        configuratorConfigurationKey(configuration.productCode),
      );
      const belongsToSubmittedConfiguration = (
        candidate: ConfiguratorRuntimeQueryData | undefined,
      ): candidate is ConfiguratorRuntimeQueryData => candidate?.configuration.configId === configuration.configId;
      if (current && !belongsToSubmittedConfiguration(current)) {
        return;
      }
      if (current) {
        queryClient.setQueryData<ConfiguratorRuntimeQueryData>(
          configuratorConfigurationKey(configuration.productCode),
          (latest) => {
            if (!belongsToSubmittedConfiguration(latest)) {
              return latest;
            }
            return {
              ...latest,
              configuration: { ...latest.configuration, configId: response.configId, attachment: undefined },
            };
          },
        );
      }
      commerce.markSubmitted(response.configId);
      dispatch(invalidateMinicart());
      configuratorV2SummaryAnalytics.addedToCart(analyticsInput);
      setSuccessItems(response.responseItems);
      setActiveDialog("success");
    } catch (error) {
      console.error(error);
      setAddToCartError(t("page.linear.addToCart.text.error"));
    } finally {
      setAddToCartPending(false);
    }
  };

  const interactionNode = (
    <>
      <RequestAQuoteDialog
        open={activeDialog === "requestQuotation"}
        countryOptions={countryOptions}
        onClose={closeDialog}
        onSubmit={async (formData: RequestAQuoteFormData, { setError, onClose }) => {
          try {
            await requestConfiguratorV2Quotation(configuration.productCode, commerce.quantity, formData, pdfData);
            onClose();
          } catch (error) {
            console.error(error);
            setError("root", { message: t("feature.configurator.requestQuote.submit.error") });
          }
        }}
      />
      <ConfigurationNameDialog
        open={activeDialog === "requestOffer" || activeDialog === "saveConfiguration"}
        title={t(activeDialog === "requestOffer" ? "page.linear.requestOffer.header" : "page.linear.saveConfig.header")}
        label={t("page.linear.saveConfig.label")}
        requiredMessage={t("page.linear.saveConfig.helperLabel")}
        mandatoryText={t("editWishlistNamePopup.mandatory")}
        cancelLabel={t("cancel")}
        submitLabel={t(activeDialog === "requestOffer" ? "linearConfigSummary.card.requestOffer" : "generic.save")}
        submitError={t("page.linear.saveConfig.result.failure")}
        onClose={closeDialog}
        onSubmit={submitName}
      />
      <RequestConfigurationQuoteDialog
        open={activeDialog === "addToQuote"}
        title={t("linearConfigSummary.card.addToQuote")}
        body={t("linear.request.quote.popup.body")}
        recipientLabel={t("linear.request.quote.popup.email.label")}
        recipient={session?.commerceContext?.linearConfiguratorEmail ?? ""}
        noteLabel={t("linear.request.quote.popup.input.label")}
        info={t("linear.request.quote.popup.email.info")}
        mandatoryText={t("linear.request.quote.popup.mandatory")}
        cancelLabel={t("cancel")}
        submitLabel={t("linearConfigSummary.card.addToQuote")}
        submitError={t("hybrisAjaxError")}
        onClose={closeDialog}
        onSubmit={async (recipientNote) => {
          await requestConfiguratorV2Quote(
            configuration.productCode,
            configuration.configId,
            recipientNote,
            configuration.currentStepNumber,
            configuration.productShortDesignation,
          );
          commerce.markSubmitted();
          redirectToUrl(LINEAR_CONFIGURATION_REQUEST_QUOTE_CONFIRMATION_PAGE_URL);
        }}
      />
      <ConfiguredProductAddToCartDialog
        open={activeDialog === "addToCart"}
        designation={configuration.productShortDesignation}
        priceText={priceText}
        quantity={commerce.quantity}
        priceReference={commerce.priceReference}
        priceReferences={commerce.priceReferences}
        priceReferenceEditable={commerce.priceReferenceEditable}
        pending={addToCartPending || commerce.isSettling}
        error={addToCartError}
        onQuantityChange={commerce.setQuantity}
        onPriceReferenceChange={commerce.setPriceReference}
        onClose={closeDialog}
        onConfirm={() => void submitAddToCart()}
      />
      {activeDialog === "success" && renderCartSuccess({ onClose: closeDialog, responseItems: successItems })}
    </>
  );

  return { triggerAction, interactionNode, directPending, directError };
};
